import React, { useState, useEffect } from 'react';
import {
  Box,
  Tooltip,
  ClickAwayListener,
  Typography,
  Link,
  keyframes,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

// Pulsing animation
const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.5);
    opacity: 0.5;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const ripple = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  100% {
    transform: scale(2.5);
    opacity: 0;
  }
`;

interface HotspotProps {
  /** Unique ID for this hotspot - used to track dismissal in localStorage */
  id: string;
  /** Tooltip content to show */
  title: string;
  /** Optional longer description */
  description?: string;
  /** Link to help article - format: "categorySlug/articleSlug" */
  learnMoreLink?: string;
  /** Size of the hotspot dot in pixels */
  size?: number;
  /** Position offset from the target element */
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  /** Whether to show the hotspot (useful for conditional display) */
  show?: boolean;
  /** Callback when hotspot is dismissed */
  onDismiss?: () => void;
}

/**
 * Hotspot component - a pulsing indicator for feature discovery.
 * Shows a tooltip on hover/tap and can be dismissed by clicking.
 * Dismissal state is persisted in localStorage.
 */
export const Hotspot: React.FC<HotspotProps> = ({
  id,
  title,
  description,
  learnMoreLink,
  size = 12,
  position = 'top-right',
  show = true,
  onDismiss,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Check localStorage on mount
  useEffect(() => {
    const dismissedHotspots = JSON.parse(localStorage.getItem('dismissedHotspots') || '[]');
    if (dismissedHotspots.includes(id)) {
      setDismissed(true);
    }
  }, [id]);

  const handleDismiss = () => {
    const dismissedHotspots = JSON.parse(localStorage.getItem('dismissedHotspots') || '[]');
    if (!dismissedHotspots.includes(id)) {
      dismissedHotspots.push(id);
      localStorage.setItem('dismissedHotspots', JSON.stringify(dismissedHotspots));
    }
    setDismissed(true);
    setOpen(false);
    onDismiss?.();
  };

  const handleLearnMoreClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (learnMoreLink) {
      sessionStorage.setItem('helpCenterTarget', learnMoreLink);
      navigate('/help');
    }
    handleDismiss();
  };

  const handleTooltipToggle = () => {
    setOpen((prev) => !prev);
  };

  const handleTooltipClose = () => {
    setOpen(false);
  };

  // Don't render if dismissed or not shown
  if (dismissed || !show) {
    return null;
  }

  // Position styles
  const positionStyles: Record<string, React.CSSProperties> = {
    'top-right': { top: -4, right: -4 },
    'top-left': { top: -4, left: -4 },
    'bottom-right': { bottom: -4, right: -4 },
    'bottom-left': { bottom: -4, left: -4 },
  };

  const tooltipContent = (
    <Box sx={{ maxWidth: 220 }}>
      <Typography variant="body2" fontWeight={500} sx={{ mb: description ? 0.5 : 0 }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {description}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        {learnMoreLink && (
          <Link
            component="button"
            variant="caption"
            onClick={handleLearnMoreClick}
            sx={{
              color: 'primary.light',
              textDecoration: 'underline',
              cursor: 'pointer',
              '&:hover': { color: 'primary.main' },
            }}
          >
            Learn more
          </Link>
        )}
        <Link
          component="button"
          variant="caption"
          onClick={handleDismiss}
          sx={{
            color: 'text.secondary',
            textDecoration: 'none',
            cursor: 'pointer',
            '&:hover': { color: 'text.primary' },
          }}
        >
          Got it
        </Link>
      </Box>
    </Box>
  );

  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <Tooltip
        title={tooltipContent}
        open={open}
        onClose={handleTooltipClose}
        disableFocusListener
        disableHoverListener={isMobile}
        disableTouchListener
        arrow
        placement="top"
        slotProps={{
          tooltip: {
            sx: {
              bgcolor: 'background.paper',
              color: 'text.primary',
              boxShadow: 3,
              p: 1.5,
              '& .MuiTooltip-arrow': {
                color: 'background.paper',
              },
            },
          },
        }}
      >
        <Box
          onClick={handleTooltipToggle}
          onMouseEnter={isMobile ? undefined : () => setOpen(true)}
          onMouseLeave={isMobile ? undefined : () => setOpen(false)}
          sx={{
            position: 'absolute',
            ...positionStyles[position],
            width: size,
            height: size,
            cursor: 'pointer',
            zIndex: 1,
          }}
        >
          {/* Main dot */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: size,
              height: size,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              animation: `${pulse} 2s ease-in-out infinite`,
            }}
          />
          {/* Ripple effect */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: size,
              height: size,
              borderRadius: '50%',
              border: '2px solid',
              borderColor: 'primary.main',
              animation: `${ripple} 2s ease-out infinite`,
            }}
          />
        </Box>
      </Tooltip>
    </ClickAwayListener>
  );
};

/**
 * Hook to check if a hotspot has been dismissed
 */
export function useHotspotDismissed(id: string): boolean {
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const dismissedHotspots = JSON.parse(localStorage.getItem('dismissedHotspots') || '[]');
    setDismissed(dismissedHotspots.includes(id));
  }, [id]);

  return dismissed;
}

/**
 * Reset all hotspots (for testing or onboarding reset)
 */
export function resetAllHotspots(): void {
  localStorage.removeItem('dismissedHotspots');
}

export default Hotspot;
